import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { CurrentUserService } from "../services/current-user.service";
import { CurrentAccountService } from "../services/current-account.service";
import { AuthTokenService } from '../services/auth-token.service';
import { AuthService } from '../services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { appConstants } from '../app-constants';
import { ToastService } from '../toast/toast.service';
import { NgxTurnstileComponent } from 'ngx-turnstile';

/**
 * LoginComponent is a component that handles the login functionality for the application.
 *
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: false
})
export class LoginComponent implements OnInit {
  form!: FormGroup;
  tanForm!: FormGroup;
  submitted = false;
  tanFormSubmitted = false;
  invalidUserOrPassword = false
  tanFormVisible = false
  tanErrors = []
  tanMethod: 'authenticator' | 'email' = 'authenticator';
  signupUrl: string;
  private returnUrl: string | null = null;
  @ViewChild(NgxTurnstileComponent) turnstile?: NgxTurnstileComponent;
  turnstileSiteKey = environment.turnstileSiteKey;
  captchaToken: string | null = null;
  locale = $localize.locale

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private currentAccountService: CurrentAccountService,
    private currentUserService: CurrentUserService,
    private authTokenService: AuthTokenService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private toastService: ToastService,
  ) {
    this.signupUrl = this.getSignupUrl();
  }

  private getSignupUrl(): string {
    if (environment.registerNewAccountUrl) {
     return environment.registerNewAccountUrl;
    } else {
      return '/signup';
    }
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      username: ['', Validators.required],
      password: [''],
      remember_me: ['']
    });

    this.tanForm = this.formBuilder.group({
      tan: ['', Validators.required],
    });

    this.returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
  }

  private async submitLoginInformation(path: string, body: string): Promise<Response> {
    return await fetch(environment.legacyAppUrl + path, {
      headers: {
        'accept': 'application/json',
        'content-type': 'application/x-www-form-urlencoded',
        'X-CSRF-Token': await this.authTokenService.getAuthenticityToken(true)
      },
      body: body,
      method: 'POST',
      credentials: 'include'
    });
  }

  /**
   * Submits the form data and handles the response from the API.
   *
   * @async
   * @function onSubmit
   * @returns {Promise} A Promise that resolves when the API responds with data.
   */
  async onSubmit(): Promise<any> {
    this.submitted = true;

    // stop here if form is invalid
    if (this.form.invalid) {
      return;
    }

    let body = new URLSearchParams({
      "user[login]": this.form.value.username,
      "user[password]": this.form.value.password,
      "user[remember_me]": this.form.value.remember_me ? "1" : "0",
      "cf-turnstile-response": this.captchaToken || ""
    }).toString()

    await this.handleResponse(await this.submitLoginInformation("/users/sign_in", body));
  }

  /**
   * Submits the TAN form to the server.
   *
   * @async
   * @returns {void}
   */
  async onTanSubmit(): Promise<void> {
    this.tanFormSubmitted = true;

    // stop here if form is invalid
    if (this.tanForm.invalid) {
      return;
    }

    const body = new URLSearchParams({
      "tan": this.tanForm.value.tan,
      "user[remember_me]": this.form.value.remember_me ? "1" : "0"
    }).toString()

    await this.handleResponse(await this.submitLoginInformation("/users/sign_in", body));
  }

  /**
   * Handles the response from an API request.
   *
   * @param {Response} response - The response object received from the API request.
   * @return {Promise} - A promise that resolves once the response has been handled.
   */
  private async handleResponse(response: Response) {
    switch(response.status) {
      case 201:
        this.handleUserCreated();
        break;
      case 401:
        this.handleUnauthorized();
        break;
      case 429:
        this.handleTooManyRequests();
        break;
      case 200:
        await this.handleOk(response);
        break;
    }
  }

  private handleTooManyRequests() {
    this.toastService.show('info', $localize`Too many requests. Please try again later.`)
    this.submitted = false
    this.form.markAsPristine()
    this.resetCaptcha()
  }

  /**
   * Handles the event when a user is created.
   * This method updates the current account and user, and navigates to the home page.
   * @return {void}
   */
  private handleUserCreated(): void {
    this.authService.completeLoginAndRedirect(this.returnUrl);
  }

  /**
   * Handles unauthorized access by setting the "invalidUserOrPassword" flag to true and hiding the TAN form.
   *
   * @private
   * @return {void} The method does not return a value.
   */
  private handleUnauthorized(): void {
    this.invalidUserOrPassword = true;
    this.tanFormVisible = false;
    this.tanMethod = 'authenticator';
    this.submitted = false;
    this.form.markAsPristine()
    this.resetCaptcha();
  }

  /**
   * Handles the response when it is successful.
   *
   * @param {Response} response - The response from the server.
   *
   * @return {Promise<void>} The promise that resolves when the handling is completed.
   */
  private async handleOk(response: Response): Promise<void> {
    let response_json = await response.json();
    if(response_json.status == "tan_required" || response_json.status == "email_tan_required") {
      this.tanFormVisible = true;
      this.tanErrors = response_json.errors;
      this.tanMethod = response_json.status === "email_tan_required" ? "email" : "authenticator";
      if (this.tanErrors.length > 0) {
        this.tanFormSubmitted = false;
        this.tanForm.markAsPristine();
      }
    } else if (response_json.status == "tan_retries_reached") {
      this.tanFormVisible = true;
      this.tanErrors = response_json.errors;
    } else if (response_json.status == "turnstile_failed") {
      this.toastService.show('info', response_json.error);
      this.submitted = false;
      this.resetCaptcha()
    } else if (response_json.status == "user_locked") {
      this.toastService.show('warning', response_json.error);
      this.submitted = false;
      this.resetCaptcha()
    } else if (
      'login' in response_json && response_json.login == null &&
      'password' in response_json && response_json.password == null
    ) { // User is access locked
      this.handleUnauthorized();
    } else {
      this.tanFormVisible = false;
      this.tanMethod = 'authenticator';
      this.form.reset();
      this.submitted = false;
      this.resetCaptcha()
      await this.authService.completeLoginAndRedirect(this.returnUrl);
    }
  }

  logoUrl(): string {
    return appConstants.LOGO_URL;
  }

  onCaptchaResolved(token: string | null): void {
    this.captchaToken = token;
  }

  private resetCaptcha(): void {
    this.captchaToken = null;
    this.turnstile?.reset();
  }
}
